import { isEqual } from 'lodash'
import { useEffect, useState, useRef } from 'react'

export default function usePromise<T>(
  promiseThunk: () => Promise<T>,
  deps: any[] = []
): {
  loading: boolean
  error: Error | null
  value: T | undefined
} {
  const [state, setState] = useState<{
    loading: boolean
    error: Error | null
    value: T | undefined
  }>({
    loading: !!promiseThunk,
    error: null,
    value: undefined
  })

  const mountedDeps = useRef<any[] | null>(deps)

  useEffect(() => {
    if (!promiseThunk) {
      setState({
        loading: false,
        error: null,
        value: undefined
      })
    } else {
      if (state.loading === false) {
        setState({
          loading: true,
          error: null,
          value: undefined
        })
      }
      let promise: Promise<T>
      if (typeof promiseThunk === 'function') {
        promise = promiseThunk()
        mountedDeps.current = deps
      } else {
        throw new Error('first parameter must be a function')
      }
      setState({
        loading: true,
        error: null,
        value: undefined
      })

      promise
        .then((value) => {
          if (mountedDeps.current) {
            setState({
              loading: false,
              error: null,
              value
            })
          }
        })
        .catch((error) => {
          if (mountedDeps.current) {
            setState({
              loading: false,
              error,
              value: undefined
            })
          }
        })
    }

    return () => {
      mountedDeps.current = null
    }
  }, deps)

  if (!isEqual(mountedDeps.current, deps)) {
    return {
      loading: true,
      error: null,
      value: undefined
    }
  }

  return state
}
